"use client";

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Navigation from '@/components/Navigation';
import CountrySelectModal, { useCountry } from '@/components/CountrySelectModal';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, MapPin, CircleDollarSign, Sparkles, Loader2, Package, Tag, Filter, X, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import Link from 'next/link';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

export default function Marketplace() {
  const { toast } = useToast();
  const { country, setCountry } = useCountry();
  const [publicListings, setPublicListings] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [aiSearching, setAiSearching] = useState(false);
  const [countryModalOpen, setCountryModalOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchListings();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/api/catalog/categories/`);
      const data = await res.json();
      setCategories(data.results || data || []);
    } catch (e) {
      console.error("Failed to fetch categories", e);
    }
  };

  const fetchListings = async (query = '', categoryId: any = null, countryCode?: string) => {
    setLoading(true);
    try {
      let url = `${API}/api/marketplace/public-listings/`;
      const params = new URLSearchParams();
      if (query) params.append('search', query);
      if (categoryId) params.append('category', categoryId);
      // Apply country filter if a country is selected
      const resolvedCountry = countryCode ?? country?.code;
      if (resolvedCountry) params.append('country_code', resolvedCountry);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const res = await fetch(url);
      const data = await res.json();
      setPublicListings(data.results || data || []);
    } catch (e) {
      console.error("Failed to fetch listings", e);
    } finally {
      setLoading(false);
    }
  };

  const handleAiSearch = async () => {
    if (!searchQuery) {
      fetchListings();
      return;
    }
    setAiSearching(true);
    try {
      const res = await fetch(`${API}/api/marketplace/public-listings/smart-search/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery })
      });
      const data = await res.json();
      if (data.keywords && data.keywords.length > 0) {
        toast({ title: '🤖 بحث ذكي', description: `تم تحليل طلبك إلى كلمات مفتاحية: ${data.keywords.join(', ')}` });
        fetchListings(data.keywords.join(' '));
      } else {
        fetchListings(searchQuery);
      }
    } catch {
      fetchListings(searchQuery);
    } finally {
      setAiSearching(false);
    }
  };

  const handleCategorySelect = (category: any) => {
    if (selectedCategory?.id === category.id) {
      setSelectedCategory(null);
      fetchListings(searchQuery, null);
    } else {
      setSelectedCategory(category);
      fetchListings(searchQuery, category.id);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    fetchListings();
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navigation title="السوق العام (الحراج)" />

      {/* Country Filter Banner */}
      {country?.code && (
        <div className="mx-4 mt-2 mb-0 flex items-center justify-between px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-2xl">
          <div className="flex items-center gap-2">
            <span className="text-lg">{country.emoji}</span>
            <span className="text-sm font-bold text-blue-800">تعرض إعلانات: {country.name_ar}</span>
          </div>
          <button
            onClick={() => setCountryModalOpen(true)}
            className="text-xs text-blue-600 font-bold hover:underline flex items-center gap-1"
          >
            <Globe className="h-3 w-3" /> تغيير
          </button>
        </div>
      )}

      <CountrySelectModal
        isOpen={countryModalOpen}
        onClose={() => setCountryModalOpen(false)}
        onSelect={(c) => { setCountry(c); fetchListings(searchQuery, selectedCategory?.id, c.code); }}
        showAllOption
      />

      <div className="p-4 max-w-7xl mx-auto space-y-6 pb-24">
        {/* Search Bar & AI Search */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 h-5 w-5" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchListings(searchQuery, selectedCategory?.id)}
              className="pr-12 rounded-2xl bg-white border-none shadow-sm h-14 text-lg"
              placeholder="ابحث عن أي شيء (جوال، أثاث، خدمات...)"
            />
          </div>
          <Button
            onClick={handleAiSearch} disabled={aiSearching}
            className="h-14 rounded-2xl px-6 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg shadow-indigo-500/25 flex gap-2 font-bold transition-all"
          >
            {aiSearching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
            <span className="hidden sm:inline">بحث ذكي</span>
          </Button>
        </div>

        {/* Categories Carousel */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Filter className="w-4 h-4" />
              تصفح حسب التصنيف
            </h3>
            {(searchQuery || selectedCategory) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-slate-500 hover:text-red-500 flex gap-1">
                <X className="w-3 h-3" /> مسح الفلاتر
              </Button>
            )}
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x">
            {categories.map((cat) => (
              <div
                key={cat.id}
                onClick={() => handleCategorySelect(cat)}
                className={`flex flex-col items-center gap-3 min-w-[90px] snap-center cursor-pointer transition-all ${selectedCategory?.id === cat.id ? 'scale-105' : 'opacity-80 hover:opacity-100'}`}
              >
                <div className={`relative w-20 h-20 rounded-[1.5rem] flex items-center justify-center shadow-sm p-4 border transition-all group ${selectedCategory?.id === cat.id ? 'bg-primary border-primary shadow-primary/20' : 'bg-white border-slate-100/50 hover:border-indigo-500/50'}`}>
                  <span className={`text-2xl ${selectedCategory?.id === cat.id ? 'text-white' : 'text-slate-600'}`}>
                    {cat.icon === 'Smartphone' ? '📱' : cat.icon === 'Laptop' ? '💻' : cat.icon === 'Home' ? '🏠' : cat.icon === 'Shirt' ? '👕' : cat.icon === 'Dog' ? '🐾' : '📦'}
                  </span>
                </div>
                <span className={`text-xs font-bold ${selectedCategory?.id === cat.id ? 'text-primary' : 'text-slate-700'}`}>{cat.name_ar}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Dynamic Public Listings Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
              <Tag className="w-6 h-6 text-emerald-500" />
              {selectedCategory ? `${selectedCategory.name_ar}` : searchQuery ? `نتائج البحث عن "${searchQuery}"` : 'أحدث الإعلانات'}
            </h2>
            <Link href="/marketplace/new">
              <Button className="rounded-xl h-10 font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20">إضافة إعلان</Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <Card key={i} className="h-80 animate-pulse bg-slate-200/50 rounded-[2rem] border-none" />
              ))}
            </div>
          ) : publicListings.length === 0 ? (
            <Card className="rounded-[2.5rem] border-none shadow-sm bg-white p-12 text-center text-slate-400">
              <Package className="w-20 h-20 mx-auto mb-6 opacity-20" />
              <p className="text-xl font-bold text-slate-500">لا توجد إعلانات متاحة حالياً</p>
              <p className="text-sm text-slate-400 mt-2">كن أول من يضيف إعلاناً في هذا التصنيف!</p>
              <Link href="/marketplace/new" className="mt-6 inline-block">
                <Button variant="outline" className="rounded-full border-primary text-primary hover:bg-primary/5">أضف إعلانك الآن</Button>
              </Link>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publicListings.map(listing => (
                <Link href={`/marketplace/listing/${listing.id}`} key={listing.id}>
                  <Card className="overflow-hidden border-none shadow-md rounded-[2.5rem] bg-white group hover:shadow-2xl transition-all cursor-pointer">
                    <div className="relative h-60 w-full bg-slate-100 overflow-hidden">
                      {listing.images && listing.images.length > 0 ? (
                        <img src={listing.images[0].image} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt={listing.title} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center opacity-30 bg-indigo-50">
                          <Package className="w-12 h-12 text-indigo-200" />
                        </div>
                      )}
                      <div className="absolute top-4 right-4 flex gap-2">
                        <Badge className="bg-white/90 text-slate-800 border-none shadow-md font-bold px-3 py-1 backdrop-blur-sm">
                          {listing.condition === 'new' ? 'جديد' : listing.condition === 'refurbished' ? 'مجدد' : 'مستعمل'}
                        </Badge>
                        {listing.is_featured && (
                          <Badge className="bg-amber-400 text-white border-none shadow-md font-bold px-3 py-1">مميز</Badge>
                        )}
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <div className="bg-emerald-600 text-white font-black px-4 py-2 rounded-2xl shadow-lg shadow-emerald-600/30">
                          {listing.price} <span className="text-[10px] font-bold opacity-80">ر.س</span>
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <div className="mb-4">
                        <span className="text-[10px] font-black text-primary uppercase tracking-tighter bg-blue-50 px-2 py-1 rounded-lg mb-2 inline-block">
                          {listing.category_name || 'عام'}
                        </span>
                        <h3 className="text-xl font-bold font-headline leading-tight line-clamp-2 text-slate-800">{listing.title}</h3>
                      </div>

                      <div className="flex items-center gap-4 text-slate-500 text-xs mb-6">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 text-slate-400" />
                          <span>{listing.location || 'السعودية'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <CircleDollarSign className="h-3.5 w-3.5 text-slate-400" />
                          <span>{listing.condition === 'new' ? 'جديد' : 'مستعمل'}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 border-t border-slate-50 pt-5">
                        <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-black text-xs border-2 border-white shadow-sm">
                          {listing.seller_name?.[0] || 'U'}
                        </div>
                        <div className="flex-1">
                          <p className="text-[10px] font-bold text-slate-400 uppercase">المعلن</p>
                          <p className="text-xs font-black text-slate-700">{listing.seller_name || 'مستخدم'}</p>
                        </div>
                        <Button className="rounded-xl bg-slate-900 border-none hover:bg-primary h-10 px-4 text-xs font-bold transition-all shadow-lg hover:shadow-primary/20">
                          التفاصيل
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
